import type { UseFormReturn } from 'react-hook-form';

import { useId, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useClients } from '~/api/clients';
import { Button, Dialog } from '~/components';
import { Field } from '~/components/Form';

import type { ClientCardDomain } from './SelectedClientCard/SelectedClientCard';

import { ClientPickerDialog } from './ClientPickerDialog/ClientPickerDialog';
import { SelectedClientCard } from './SelectedClientCard/SelectedClientCard';
import { useClientContactSelection } from './useClientContactSelection';

type FormWithClientContact = {
  clientId: number | null;
  condition: { id: number; type: 'condition' | 'customAgreement' | 'customCondition' } | null;
  contactId: number | null;
  specialConditions: string;
};

type Props<T extends FormWithClientContact> = {
  domain: ClientCardDomain;
  form: T extends FormWithClientContact ? UseFormReturn<T> : never;
  required?: boolean;
};

export const FormClient = <T extends FormWithClientContact>({ domain, form, required = true }: Props<T>) => {
  const { data: clients } = useClients();
  const { t } = useTranslation(['invoices', 'validation']);

  const { control, watch } = form;
  const { clearClientContact } = useClientContactSelection(form);

  const clientPickerDialogHandle = Dialog.useHandle();
  const clientControlId = useId();

  const watchClientId = watch('clientId');
  const watchContactId = watch('contactId');

  const selectedClient = useMemo(() => clients.find(({ id }) => id === watchClientId) ?? null, [clients, watchClientId]);
  const selectedContact = useMemo(
    () => selectedClient?.contacts.find(({ id }) => id === watchContactId) ?? null,
    [selectedClient, watchContactId],
  );

  return (
    <>
      <Controller
        control={control}
        name="clientId"
        render={({ field: { ref }, fieldState: { error, invalid, isDirty, isTouched } }) => (
          <Field.Root dirty={isDirty} invalid={invalid} name="clientId" touched={isTouched}>
            <Field.Label htmlFor={clientControlId} optional={!required}>
              {t('invoices:fields.clientId.label')}
            </Field.Label>

            {selectedClient && selectedContact ? (
              <SelectedClientCard
                client={selectedClient}
                contact={selectedContact}
                controlId={clientControlId}
                controlRef={ref}
                domain={domain}
                form={form}
                onRemove={() => clearClientContact(selectedContact)}
              />
            ) : (
              <div>
                <Dialog.Trigger
                  handle={clientPickerDialogHandle}
                  id={clientControlId}
                  ref={ref}
                  render={
                    <Button emphasis="primary" icon="Add">
                      {t('invoices:fields.clientId.add')}
                    </Button>
                  }
                />
              </div>
            )}

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Root>
        )}
        rules={required ? { required: { value: true, message: t('validation:required') } } : undefined}
      />

      <Dialog.Root handle={clientPickerDialogHandle}>
        <ClientPickerDialog form={form} onClose={() => clientPickerDialogHandle.close()} />
      </Dialog.Root>
    </>
  );
};
